// Cached result markup is a full section render, so it is worth keeping, but it is also unbounded
// today: every keystroke adds an entry and nothing is ever evicted. A shopper typing a long
// hockey query would accumulate one entry per prefix for the life of the page. Cap it and evict in
// insertion order, which for string keys is the order Object.keys() reports.
const MAX_CACHED_SEARCH_RESULTS = 20;

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    // Set from the markup we just rendered; see captureSearchForTemplate().
    this.searchForTemplate = null;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  /*
   * Keeps the "Search for <term>" button label in step with the input while the shopper types.
   *
   * This used to do `currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1` and bail out
   * when the term appeared more than once. Both halves were broken:
   *   - previousTerm is raw user input, so any regex metacharacter threw a SyntaxError inside the
   *     debounced onChange handler. "+1.5 flex" plus one more keystroke builds /+1.5 f/, the throw
   *     happened before getSearchResults() was reached, and the dropdown stopped responding until
   *     reload. Same for (, ), [, *, \ and every other metacharacter hockey queries contain.
   *   - String.prototype.match() returns null for an absent term, and null.length is a TypeError.
   *   - An empty term matches at every position, so the count was always > 1 and the label was
   *     frozen on the server-rendered term until the second keystroke.
   *
   * None of that is needed. We know exactly which characters belong to the term: this component
   * rendered (or last rendered) the markup with a known term, so the label is prefix + term +
   * suffix. Capture that once and re-compose the label, instead of searching a translated sentence
   * for a substring and hoping it is the right substring.
   */
  captureSearchForTemplate(renderedForTerm) {
    const element = this.querySelector('[data-predictive-search-search-for-text]');
    const text = element?.textContent;
    if (!element || !text || !renderedForTerm) return;

    const start = this.findDelimitedTerm(text, renderedForTerm);
    // No unambiguous occurrence means we cannot tell the term from the surrounding copy, and
    // guessing would rewrite a word the shopper never typed. Staying a no-op is the safe failure.
    if (start === -1) {
      this.searchForTemplate = null;
      return;
    }

    this.searchForTemplate = {
      element,
      prefix: text.slice(0, start),
      suffix: text.slice(start + renderedForTerm.length),
    };
  }

  /*
   * Index of `term` inside `text` only when it sits at a word boundary, so a one-character term
   * ("c", "1", "+") cannot match inside a longer word. Returns -1 when there is no delimited match.
   * The label wraps the term in typographic quotes, so a real term is always delimited; checking for
   * it explicitly keeps this correct for locales that quote differently.
   */
  findDelimitedTerm(text, term) {
    // undefined means end-of-string, which is a boundary; a quote, bracket or space is the other
    // case that can legitimately flank a search term.
    const isBoundary = (character) =>
      character === undefined ||
      /^[\s\u201c\u201d\u2018\u2019"'(),.:;!?/\\-]$/.test(character);

    let from = 0;
    while (from <= text.length - term.length) {
      const at = text.indexOf(term, from);
      if (at === -1) return -1;
      if (isBoundary(text[at - 1]) && isBoundary(text[at + term.length])) return at;
      from = at + 1;
    }
    return -1;
  }

  // previousTerm is accepted and ignored on purpose: the label is rebuilt from the captured
  // prefix/suffix, so there is no substring to locate and nothing to get wrong.
  updateSearchForTerm(previousTerm, newTerm) {
    const template = this.searchForTemplate;
    if (!template) return;
    template.element.textContent = `${template.prefix}${newTerm}${template.suffix}`;
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    // Collision-free key. `searchTerm.replace(' ', '-')` replaced only the *first* space, so
    // "ccm t100" and "ccm-t100" produced the same key and one served the other's markup. Because
    // the cache write fans out to every <predictive-search> on the page (see below), a single
    // collision also poisoned the header modal from the /search page.
    const queryKey = encodeURIComponent(searchTerm.toLowerCase());
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(searchTerm, this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Deliberate: this storefront renders more than one <predictive-search> (the header
        // dropdown and the /search page field), and both hit the same section render with the same
        // query. Writing every instance means the second one to be typed into serves markup that
        // has already been fetched. It is only safe because the key is derived from the exact
        // lowercased term -- a lossy key would cross-serve results between different queries.
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          PredictiveSearch.cacheResult(predictiveSearchInstance.cachedResults, queryKey, resultsMarkup);
        });
        this.renderSearchResults(searchTerm, resultsMarkup);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  // Evicts in insertion order once the cap is reached. String keys keep their insertion order in
  // Object.keys(), so the first key is always the oldest entry.
  static cacheResult(cache, queryKey, resultsMarkup) {
    if (!Object.prototype.hasOwnProperty.call(cache, queryKey)) {
      while (Object.keys(cache).length >= MAX_CACHED_SEARCH_RESULTS) {
        delete cache[Object.keys(cache)[0]];
      }
    }
    cache[queryKey] = resultsMarkup;
  }

  renderSearchResults(renderedForTerm, resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    // Now that the label is in the DOM with a known term, re-derive the prefix/suffix used by
    // updateSearchForTerm() for the next keystroke.
    this.captureSearchForTemplate(renderedForTerm);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
